import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Copy, Loader2, AlertCircle, RefreshCw, Settings } from 'lucide-react';
import Navbar from './shared/Navbar.jsx';
import ProfileDetails from './ProfileDetails.jsx';
import ShowMatchTable from './ShowMatchTable.jsx';
import { sharedStyles, createButton, createGlassCard } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

/**
 * ShowMatchReport - Display show recommendations for guests
 *
 * GUEST-SIDE mirror of GuestReport.jsx. Shows recommended podcasts
 * for a guest based on their profile and expertise.
 */
function ShowMatchReport() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const { makeRequest } = useApi();

  const [copiedIndex, setCopiedIndex] = useState(null);
  const [pitchCopied, setPitchCopied] = useState(false);
  const [selectedShowIndex, setSelectedShowIndex] = useState(0);
  const [showPitch, setShowPitch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [userTier, setUserTier] = useState('starter');
  const [isLifetime, setIsLifetime] = useState(false);
  const [generatingMatch, setGeneratingMatch] = useState(false);

  // Fetch match and profile data from API
  useEffect(() => {
    const fetchMatchData = async () => {
      if (!isLoaded) return;

      if (!isSignedIn) {
        navigate('/sign-in?redirect_url=' + encodeURIComponent(`/guest/show-matches/${matchId}`));
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch match (with profile) and user data in parallel
        const [matchWithProfile, userData] = await Promise.all([
          makeRequest(`guest/show-matches/${matchId}?include_profile=true`),
          makeRequest('users/me')
        ]);

        setMatchData(matchWithProfile);
        setUserTier(userData.subscription_tier || 'starter');
        setIsLifetime(userData.is_lifetime || false);

        // Profile data is included in the match response
        if (matchWithProfile.guest_profile) {
          setProfileData(matchWithProfile.guest_profile);
        }

        setLoading(false);

      } catch (err) {
        console.error('Error fetching show matches:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchMatchData();
  }, [matchId, isLoaded, isSignedIn, navigate, makeRequest]);

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1rem',
        }}>
          <Loader2 size={48} style={{
            color: sharedStyles.colors.primary,
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{
            color: sharedStyles.colors.textSecondary,
            fontSize: '1.125rem',
          }}>
            Loading your show recommendations...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1.5rem',
          padding: '2rem',
        }}>
          <div style={{
            ...createGlassCard('default'),
            padding: '2rem',
            maxWidth: '500px',
            textAlign: 'center',
          }}>
            <AlertCircle size={48} style={{
              color: sharedStyles.colors.error,
              marginBottom: '1rem',
            }} />
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: sharedStyles.colors.text,
              marginBottom: '0.5rem',
            }}>
              Error Loading Recommendations
            </h2>
            <p style={{
              color: sharedStyles.colors.textSecondary,
              marginBottom: '1.5rem',
            }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/guest/dashboard')}
              style={createButton('primary')}
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!matchData || !profileData) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}>
          <p style={{ color: sharedStyles.colors.textSecondary }}>
            No show recommendations available
          </p>
        </div>
      </div>
    );
  }

  // Transform profile data for sidebar display
  const guestProfile = {
    headline: profileData.headline,
    bio: profileData.bio,
    company: profileData.company,
    title: profileData.title,
    expertiseAreas: profileData.expertise_areas || [],
    topics: profileData.topics || [],
    idealShowProfile: profileData.ideal_show_profile,
    availability: profileData.availability
  };

  // Transform show recommendations
  const showRecommendations = (matchData.recommended_shows || []).map((show, index) => ({
    id: index + 1,
    showName: show.show_name,
    hostName: show.host_name,
    description: show.show_description,
    network: show.network,
    relevanceScore: show.relevance_score,
    whyGoodFit: show.why_good_fit,
    topicsCovered: show.topics_covered || [],
    audienceSize: show.audience_size_estimate,
    showTier: show.show_tier,
    accessibilityScore: show.accessibility_score,
    contactEmail: show.contact_email || null,
    website: show.website || null,
    pitchFormUrl: show.pitch_form_url || null,
    pitch: show.pitch || null,
    discoveredAt: matchData.created_at
  }));

  const handleCopyEmail = (email, index) => {
    navigator.clipboard.writeText(email);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleViewPitch = (index) => {
    setSelectedShowIndex(index);
    setShowPitch(true);
  };

  const handleRegenerateMatch = async () => {
    setGeneratingMatch(true);

    try {
      // TODO: Implement regeneration endpoint
      // For now, just reload the current match
      const response = await makeRequest('guest/show-matches', {
        method: 'POST',
        body: JSON.stringify({})
      });

      navigate(`/guest/show-matches/${response.show_match_id}`);
    } catch (error) {
      console.error('Error generating new matches:', error);

      if (error.message.includes('402')) {
        alert('No credits available. Purchase credits to find more shows.');
      } else if (error.message.includes('500')) {
        alert('The AI service encountered a temporary error. Please try again in a few seconds.');
      } else {
        alert(`Failed to generate recommendations: ${error.message}`);
      }
      setGeneratingMatch(false);
    }
  };

  const selectedShow = showRecommendations[selectedShowIndex];
  const hasPremiumAccess = userTier === 'pro';

  return (
    <div style={sharedStyles.pageContainer}>
      <Navbar variant="report" />

      {/* Incomplete Match Banner */}
      {matchData && !matchData.is_completed && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          margin: '1.5rem 4rem 0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <AlertCircle size={24} style={{ color: sharedStyles.colors.error, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: sharedStyles.colors.error,
              marginBottom: '0.25rem'
            }}>
              Recommendation Generation Failed
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: sharedStyles.colors.error,
              lineHeight: '1.5'
            }}>
              This recommendation set did not complete successfully. Please try regenerating.
            </p>
          </div>
        </div>
      )}

      {/* Regenerate Actions */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        padding: '1.5rem 4rem 0.5rem',
      }}>
        <button
          onClick={handleRegenerateMatch}
          disabled={generatingMatch}
          style={{
            ...createButton('primary'),
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            padding: '0.75rem 1.25rem',
            opacity: generatingMatch ? 0.6 : 1,
            cursor: generatingMatch ? 'not-allowed' : 'pointer'
          }}
        >
          {generatingMatch ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Finding Shows... (30-60s)
            </>
          ) : (
            <>
              <RefreshCw size={18} />
              Find New Shows
            </>
          )}
        </button>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        padding: '1rem 4rem',
        overflow: 'visible'
      }}>
        <div style={{
          display: 'flex',
          gap: '1rem',
          maxWidth: '1200px',
          width: '100%',
          alignItems: 'stretch',
          maxHeight: 'calc(100vh - 200px)',
          minHeight: 'fit-content'
        }}>
          {/* Left Panel - Guest Profile */}
          <ProfileDetails guestProfile={guestProfile} />

          {/* Right Panel - Show Recommendations */}
          <ShowMatchTable
            showRecommendations={showRecommendations}
            copiedIndex={copiedIndex}
            onCopyEmail={handleCopyEmail}
            onViewPitch={handleViewPitch}
            hasPremiumAccess={hasPremiumAccess}
            guestName={guestProfile.headline}
          />
        </div>
      </div>

      {/* Pitch Modal */}
      {showPitch && selectedShow && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}
          onClick={() => setShowPitch(false)}
        >
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0px 32px 64px rgba(0, 0, 0, 0.2)'
          }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${sharedStyles.colors.primary}20`,
              background: sharedStyles.colors.surfaceAlt,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Pitch for {selectedShow.showName}
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: sharedStyles.colors.textSecondary,
                  margin: 0
                }}>
                  AI-generated outreach message
                </p>
              </div>
              <button
                onClick={() => {
                  const emailWithSubject = `Subject: Guest Pitch for ${selectedShow.showName}\n\n${selectedShow.pitch}`;
                  navigator.clipboard.writeText(emailWithSubject);
                  setPitchCopied(true);
                  setTimeout(() => setPitchCopied(false), 2000);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: pitchCopied ? sharedStyles.colors.success : sharedStyles.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexShrink: 0
                }}
              >
                <Copy size={16} />
                {pitchCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '1.5rem',
              overflowY: 'auto',
              maxHeight: 'calc(80vh - 140px)'
            }}>
              <div style={{
                background: sharedStyles.colors.primaryLight,
                borderRadius: '12px',
                padding: '1.25rem',
                fontSize: '0.9rem',
                lineHeight: '1.7',
                color: sharedStyles.colors.textSecondary,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                <p style={{ marginBottom: '1rem', fontWeight: '600' }}>
                  <strong>Subject:</strong> Guest Pitch for {selectedShow.showName}
                </p>

                <div style={{
                  whiteSpace: 'pre-wrap',
                  borderTop: '1px solid rgba(234, 88, 12, 0.2)',
                  paddingTop: '1rem'
                }}>
                  {selectedShow.pitch}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem',
              borderTop: '1px solid rgba(234, 88, 12, 0.1)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowPitch(false)}
                style={{
                  ...createButton('secondary')
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShowMatchReport;
